import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Page } from "components/ui";
import { isEmpty } from "ramda";
import "./tracking.scss";
import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery
} from "@mui/material";
import { API } from "actions/api";
import { formatDate, applicationDateTimeFormat, makeTrackingUrl } from "utils/misc";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import BreadCrumbs from "components/ui_new/breadcrumbs";
import OrderDetailsModal from "../orders/order-details-modal";

const Tracking = () => {
  const [trackingInfo, setTrackingInfo] = useState([]);
  const translate = useTranslate();
  const [isFetching, setIsFetching] = useState(true);
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery("(max-width: 900px)");

  useDocumentTitle(translate("trackingList"));

  const fetchTrackingInfo = async () => {
    try {
      const { trackingInfoList } = await dispatch({
        type: API,
        route: "patient_portal/mobile/tracking",
        method: "GET"
      });
      await setTrackingInfo(trackingInfoList);
      setIsFetching(false);
    } catch (error) {
      console.error(error.message);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchTrackingInfo();
  }, []);

  return (
    <Page.Container>
      <Page.Header title={translate("tracking")} goBack />
      <Page.Content
        isFetching={isFetching}
        isEmpty={!trackingInfo || trackingInfo.length === 0}
        emptyMessage={translate("noTrackingNumbers")}
      >
        {isFetching && (
          <div role="status" aria-live="polite">
            {translate("loadingTrackingInfo")}
          </div>
        )}
        <div className="tracking-page-content" id="tracking">
          <div className="page-content-header">
            <BreadCrumbs
              childLabel="tracking"
              parentLink="/orders"
              parentLinkLabel="orders"
              title={translate("trackingList")}
              titleClassname="tracking-title"
            />
          </div>

          {!isSmallScreen ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Tracking Table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">
                      <span
                        id="tracking-number-header"
                        className="table-header-title"
                      >
                        {translate("trackingNumber")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span
                        id="invoice-number-header"
                        className="table-header-title"
                      >
                        {translate("invoiceNumber")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span
                        id="shipping-date-header"
                        className="table-header-title"
                      >
                        {translate("shippingDate")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="carrier-header" className="table-header-title">
                        {translate("carrier")}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingInfo.map(row => (
                    <TableRow key={row.tracking_number}>
                      <TableCell aria-labelledby="tracking-number-header">
                        <a
                          href={makeTrackingUrl(
                            row.carrier,
                            row.tracking_number
                          )}
                          rel="noopener noreferrer"
                          target="_blank"
                          className="tracking-link-external"
                          aria-label={translate("trackPackage")}
                        >
                          {row.tracking_number}
                        </a>
                      </TableCell>
                      <TableCell aria-labelledby="invoice-number-header">
                        <OrderDetailsModal
                          orderId={row.order_id}
                          mobileCard={false}
                          customTrigger={({ openModal, fetchOrderDetails }) => (
                            <span
                              className="tracking-link"
                              onClick={e => {
                                e.stopPropagation();
                                openModal();
                                fetchOrderDetails();
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openModal();
                                  fetchOrderDetails();
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={translate("viewOrderDetails")}
                            >
                              {row.invoice_number || "-"}
                            </span>
                          )}
                        />
                      </TableCell>
                      <TableCell aria-labelledby="shipping-date-header">
                        {formatDate(
                          row.shipping_date,
                          applicationDateTimeFormat
                        )}
                      </TableCell>
                      <TableCell aria-labelledby="carrier-header">
                        {row.carrier}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="mobile-cards-container">
              {trackingInfo.map(row => (
                <div key={row.tracking_number} className="tracking-mobile-card">
                  <div className="tracking-card-content">
                    <div>
                      <div className="tracking-label">
                        {translate("trackingNumber")}
                      </div>
                      <a
                        href={makeTrackingUrl(row.carrier, row.tracking_number)}
                        rel="noopener noreferrer"
                        target="_blank"
                        className="tracking-link-external"
                        aria-label={translate("trackPackage")}
                      >
                        {row.tracking_number}
                      </a>
                    </div>
                    <div className="tracking-card-row">
                      <div>
                        <div className="tracking-label">
                          {translate("invoiceNumber")}
                        </div>
                        <OrderDetailsModal
                          orderId={row.order_id}
                          mobileCard={true}
                          customTrigger={({ openModal, fetchOrderDetails }) => (
                            <div
                              className="tracking-link"
                              onClick={e => {
                                e.stopPropagation();
                                openModal();
                                fetchOrderDetails();
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openModal();
                                  fetchOrderDetails();
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={translate("viewOrderDetails")}
                            >
                              {row.invoice_number || "-"}
                            </div>
                          )}
                        />
                      </div>
                      <div>
                        <div className="tracking-label">
                          {translate("carrier")}
                        </div>
                        <div>{row.carrier}</div>
                      </div>
                    </div>
                    <div className="tracking-card-row">
                      <div>
                        <div className="tracking-label">
                          {translate("shippingDate")}
                        </div>
                        <div>
                          {formatDate(
                            row.shipping_date,
                            applicationDateTimeFormat
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Page.Content>
    </Page.Container>
  );
};

export default Tracking;
